import React, { useState, useEffect } from 'react';
import { Box, Flex, Heading, Text, Input, Button, Table, Thead, Tbody, Tr, Th, Td, IconButton, useColorModeValue, useDisclosure, FormControl, FormLabel, Select, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter } from '@chakra-ui/react';
import { FaTrash, FaEdit, FaSyncAlt, FaPlus } from 'react-icons/fa';
const baseUrl = process.env.REACT_APP_API_BASE_URL;

const initialData = [];

function UserModal({ isOpen, onClose, onSave, user }) {
  const [form, setForm] = useState(user || { name: '', email: '', role: '', status: 'Active' });
  useEffect(() => {
    setForm(user || { name: '', email: '', role: '', status: 'Active' });
  }, [user, isOpen]);

  const modalBg = useColorModeValue('white', 'gray.700');
  const inputBg = useColorModeValue('gray.50', 'gray.600');
  const inputBorder = useColorModeValue('gray.200', 'gray.600');

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent bg={modalBg}>
        <ModalHeader>{user ? 'Edit User' : 'New User'}</ModalHeader>
        <ModalBody pb={6}>
          <FormControl mb={4}>
            <FormLabel>Name</FormLabel>
            <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Enter name" bg={inputBg} borderColor={inputBorder} />
          </FormControl>

          <FormControl mb={4}>
            <FormLabel>Email</FormLabel>
            <Input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="Enter email" disabled={!!user} bg={inputBg} borderColor={inputBorder} />
          </FormControl>

          <FormControl mb={4}>
            <FormLabel>Role</FormLabel>
            <Input value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} placeholder="Enter role" bg={inputBg} borderColor={inputBorder} />
          </FormControl>

          <FormControl mb={4}>
            <FormLabel>Status</FormLabel>
            <Select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} bg={inputBg} borderColor={inputBorder}>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </Select>
          </FormControl>
        </ModalBody>

        <ModalFooter>
          <Button onClick={onClose} mr={3} borderRadius="lg">Cancel</Button>
          <Button colorScheme="green" onClick={() => onSave(form)} borderRadius="lg">Save</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default function SettingsUsersTab() {
  const [data, setData] = useState(initialData);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { isOpen: isModalOpen, onOpen: onOpenModal, onClose: onCloseModal } = useDisclosure();
  const [editUser, setEditUser] = useState(null);

  // Chakra color mode values
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const tableHeaderBg = useColorModeValue('green.50', 'gray.700');
  const tableHeaderColor = useColorModeValue('green.700', 'green.200');
  const tableRowBgEven = useColorModeValue('white', 'gray.800');
  const tableRowBgOdd = useColorModeValue('gray.50', 'gray.700');
  const inputBg = useColorModeValue('gray.50', 'gray.700');
  const inputBorder = useColorModeValue('gray.200', 'gray.600');
 

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${baseUrl}/api/users`);
      if (!res.ok) throw new Error('Failed to fetch users');
      setData(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleAdd = async (user) => {
    setError('');
    try {
      const res = await fetch(`${baseUrl}/api/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(user),
      });
      if (!res.ok) throw new Error('Failed to add user');
      onCloseModal();
      fetchUsers();
    } catch (e) {
      setError(e.message);
    }
  };

  const handleEdit = async (user) => {
    setError('');
    try {
      const res = await fetch(`${baseUrl}/api/users/${encodeURIComponent(user.email)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(user),
      });
      if (!res.ok) throw new Error('Failed to update user');
      setEditUser(null);
      fetchUsers();
    } catch (e) {
      setError(e.message);
    }
  };

  const handleDelete = async (email) => {
    setError('');
    try {
      const res = await fetch(`${baseUrl}/api/users/${encodeURIComponent(email)}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete user');
      fetchUsers();
    } catch (e) {
      setError(e.message);
    }
  };

  const filtered = data.filter(row =>
    row.name.toLowerCase().includes(search.toLowerCase()) ||
    row.email.toLowerCase().includes(search.toLowerCase()) ||
    row.role.toLowerCase().includes(search.toLowerCase()) ||
    row.status.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box>
      <Heading as="h3" size="md" mb={2} color={textColor}>Users</Heading>
      <Text fontSize="sm" color="gray.500" mb={6}>Manage users, roles, and access.</Text>
      <Flex mb={6} justify="space-between" align="center" flexWrap="wrap" gap={4}>
        <Flex gap={2}>
          <Button leftIcon={<FaSyncAlt />} colorScheme="green" onClick={fetchUsers} size="sm" borderRadius="lg">Refresh</Button>
          <Button leftIcon={<FaPlus />} colorScheme="green" onClick={() => { setEditUser(null); onOpenModal(); }} size="sm" borderRadius="lg">New User</Button>
        </Flex>
        <Input
          placeholder="Search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          size="sm"
          borderRadius="full"
          maxW="220px"
          minW="120px"
          bg={inputBg}
          borderColor={inputBorder}
        />
      </Flex>
      {error && <Box color="red.500" mb={4}>{error}</Box>}
      {loading ? (
        <Box color="gray.500" p={8} textAlign="center">Loading users...</Box>
      ) : (
        <Box overflowX="auto" borderRadius="lg" boxShadow="md" borderWidth={1} borderColor={borderColor} bg={cardBg}>
          <Table variant="simple" size="sm">
            <Thead bg={tableHeaderBg}>
              <Tr>
                <Th color={tableHeaderColor} py={3}>Name</Th>
                <Th color={tableHeaderColor} py={3}>Email</Th>
                <Th color={tableHeaderColor} py={3}>Role</Th>
                <Th color={tableHeaderColor} py={3}>Status</Th>
                <Th color={tableHeaderColor} textAlign="right" py={3}>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filtered.map((row, i) => (
                <Tr key={row.email} bg={i % 2 === 0 ? tableRowBgEven : tableRowBgOdd}>
                  <Td color={textColor}>{row.name}</Td>
                  <Td color={textColor}>{row.email}</Td>
                  <Td color={textColor}>{row.role}</Td>
                  <Td color={textColor}>{row.status}</Td>
                  <Td textAlign="right">
                    <IconButton
                      aria-label="Delete user"
                      icon={<FaTrash />}
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => handleDelete(row.email)}
                      mr={2}
                    />
                    <IconButton
                      aria-label="Edit user"
                      icon={<FaEdit />}
                      size="sm"
                      variant="ghost"
                      colorScheme="green"
                      onClick={() => { setEditUser(row); onOpenModal(); }}
                    />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
      <UserModal isOpen={isModalOpen} onClose={onCloseModal} onSave={editUser ? handleEdit : handleAdd} user={editUser} />
    </Box>
  );
} 